import { useEffect, useRef, useState } from "react";
import './Overlay.css';
import { Action as AdvisoryAction, AdvisoryContent } from "../../../common/global-types/AdvisoryContent";
import PriceSummaryCard from "../../../common/components/price-summary-card/PriceSummaryCard";
import IconedList from "../../../common/components/iconed-list/IconedList";
import Paragraph from "../../../common/components/paragraph/Paragraph";
import Headline from "../../../common/components/headline/Headline";
import Tagline from "../../../common/components/tagline/Tagline";
import useTracking from "../../../common/tracking/useTracking";
import Action from "../../../common/components/action/Action";
import APIUtil from "../../../common/api/APIUtil";

type BlockProps = {
  content: AdvisoryContent;
  viewId: number | undefined;
  dispatchActionEvent: (action: AdvisoryAction) => void;
};

function Overlay({ content, viewId, dispatchActionEvent }: BlockProps) {
  const overlayRef = useRef<HTMLDialogElement | null>(null);
  const view = viewId ? content.contentViews?.find((cv) => cv.contentViewId === viewId) : undefined;
  const [modalOpen, setModalOpen] = useState(false);
  const trackVisibilityElement = useTracking(content, modalOpen ? viewId : undefined);

  useEffect(() => {
    if (overlayRef.current) {
      overlayRef.current.showModal();
      setModalOpen(true);
    } else {
      setModalOpen(false);
    }
  }, [viewId]);

  if (view === undefined) return null;

  const actions: JSX.Element[] = [];
  const texts: JSX.Element[] = [];
  let trackElement = true;

  for (let index = 0; index < (view.viewElements.texts || []).length; index++) {
    const text = view.viewElements.texts?.[index];

    if (!text) return;

    if (text.type === 'paragraph') {
      texts.push(
        <Paragraph key={text.type + text.value} text={text} />
      );
    }
    if (text.type === 'headline') {
      texts.push(
        <Headline key={text.type + text.value} text={text} ref={trackElement ? trackVisibilityElement : undefined} />
      );

      trackElement = false;
    }
    if (text.type === 'list') {
      const listItems = view.viewElements.texts?.filter((text) => text.type === 'list') || [];

      texts.push(
        <IconedList key={text.type + text.value} texts={listItems} />
      );

      index += listItems.length - 1;
    }
    if (text.type === 'tagline') {
      texts.push(
        <Tagline key={text.type + text.value} text={text} />
      );
    }
    if (text.type === 'card') {
      const cardItems = view.viewElements.texts?.filter((text) => text.type === 'card') || [];

      texts.push(
        <PriceSummaryCard key={text.type + text.value} texts={cardItems} />
      );

      index += cardItems.length - 1;
    }
  };

  (view.viewElements.actions || []).forEach((action, index) => {
    if (index === 0) return;

    actions.push(
      <Action 
        key={action.text}
        action={action}
        dispatchActionEvent={dispatchActionEvent}
        hasLoader={!action.url}
      />
    );
  });

  return (
    <dialog
      id="advisory-overlay"
      className="if modal-v19 align-center"
      ref={overlayRef}
    >
      <button
        aria-label="close modal-v19"
        className="if button medium tonal modal-v19-close-button"
        id="close-modal-v19"
        type="button"
        onClick={async () => { 
          const action = view?.viewElements?.actions?.[0];

          if (action) {
            if (action.callback) await APIUtil.handleCallback(`${action.callback.baseUrl}${action.callback.callbackPath}`, action.callback.payload);
            dispatchActionEvent(action);
          }
        }}
      >
        <ids-icon cross ui-24></ids-icon>
      </button>
      <div className="if modal-v19-content gap-24 medium-padding">
        <div className="overlay-top">
          {texts.slice(0, 2)}
        </div>
        <div className="overlay-main">
          <div className="if card-content">
            {texts.slice(2)}
            {actions}
          </div>
        </div>
      </div>
    </dialog>
  );
};

export default Overlay;